import React, { useContext, useEffect, useState } from 'react';
import AuthContext from '../../../../authentication/AuthContext';
import { useAlert } from 'react-alert';
import axios from 'axios';
import Chart from 'react-google-charts';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import OrderContext from '../../OrderContext';

export default function RebateDetails() {
    let cas = useContext(AuthContext);
    let { orderId, orderHeader } = useContext(OrderContext);
    let alert = useAlert();

    let [rebateDetails, setRebateDetails] = useState();

    useEffect(() => {
        axios
            .get(
                window.CONFIG['ws.activoc-web-order-api.order.rebates.summary'].replace('{orderId}', orderId),
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(response => setRebateDetails(response.data))
            .catch(() => alert.error('ERROR: Unable to retrieve rebate details.'));
        // eslint-disable-next-line
    }, [cas, orderId, orderHeader]);

    let createRebateLegend = function() {
        let currentDesc;
        let projectedDesc;
        let furtherDesc;

        currentDesc =
            'Current spend this period £' +
            rebateDetails.currentSpend +
            ' (' +
            rebateDetails.currentRebatePercentage +
            '% rebate)';
        projectedDesc =
            'Projected end of period spend £' +
            rebateDetails.predictedSpend +
            ' (' +
            rebateDetails.predictedRebatePercentage +
            '% rebate)' +
            (rebateDetails.ni ? '**' : '');
        furtherDesc =
            'Further spend required  £' +
            rebateDetails.requiredSpend +
            ' for ' +
            rebateDetails.requiredRebatePercentage +
            '% rebate';

        let legend = [
            '',
            currentDesc,
            { role: 'tooltip' },
            { role: 'style' },
            projectedDesc,
            { role: 'tooltip' },
            { role: 'style' }
        ];

        if (rebateDetails.requiredSpend > 0) {
            legend.push(furtherDesc);
            legend.push({ role: 'tooltip' });
            legend.push({ role: 'style' });
        }
        return legend;
    };

    let createRebateSeries = function() {
        let currentDesc;
        let projectedDesc;
        let furtherDesc;

        currentDesc =
            'Current spend so far this period is \u00A3' +
            rebateDetails.currentSpend +
            ' (' +
            rebateDetails.currentRebatePercentage +
            '% rebate).';
        if (rebateDetails.valueToSpend > 0) {
            currentDesc +=
                ' Spend \u00A3' +
                rebateDetails.valueToSpend +
                ' more to get the ' +
                rebateDetails.nextRebatePercentage +
                '% rebate!';
        }
        projectedDesc =
            'Your projected spend from now to period end is \u00A3' +
            rebateDetails.predictedSpend +
            ' (' +
            rebateDetails.predictedRebatePercentage +
            '% rebate)' +
            (rebateDetails.ni ? '**' : '');
        furtherDesc =
            "You'll need to spend an additional \u00A3" +
            rebateDetails.requiredSpend +
            ' on top of your projected spend to reach the ' +
            rebateDetails.requiredRebatePercentage +
            '% rebate.';

        let totalSpend = calculateTotalSpend();
        let rebateProjection = calculateRebateProjection();
        let furtherSpend = rebateDetails.requiredSpend;

        let series = [
            '',
            totalSpend,
            currentDesc,
            'color: #00573D',
            rebateProjection - totalSpend,
            projectedDesc,
            'color: #86BBD8'
        ];

        if (rebateDetails.requiredSpend > 0) {
            series.push(furtherSpend);
            series.push(furtherDesc);
            series.push('color: #D3D4D9');
        }

        return series;
    };

    let calculateRebateProjection = function() {
        let increments = rebateDetails.increments;

        let lastThreshold = increments[increments.length - 1].threshold;

        let limit = lastThreshold / 3 + lastThreshold;

        if (rebateDetails.predictedSpend > limit) {
            return limit;
        }

        return rebateDetails.predictedSpend;
    };
    let calculateTotalSpend = function() {
        let increments = rebateDetails.increments;

        if (rebateDetails.currentSpend === 0 || rebateDetails.currentSpend === rebateDetails.predictedSpend) {
            return rebateDetails.currentSpend;
        }

        let lastThreshold = increments[increments.length - 1].threshold;
        let limit = lastThreshold / 3 + lastThreshold;

        if (rebateDetails.predictedSpend > limit && rebateDetails.currentSpend > lastThreshold) {
            let aux = (rebateDetails.currentSpend * (lastThreshold / 3)) / rebateDetails.predictedSpend;
            if (aux > lastThreshold / 3 / 3) {
                return lastThreshold + lastThreshold / 3 / 3;
            }

            return (
                lastThreshold +
                (rebateDetails.currentSpend * (lastThreshold / 3)) / rebateDetails.predictedSpend
            );
        }

        return rebateDetails.currentSpend;
    };

    let createRebateChartHorizontalAxis = function() {
        let ticks = [];
        let increments = rebateDetails.increments;

        increments.forEach(increment => {
            ticks.push(createTick(increment));
        });

        let lastThreshold = increments[increments.length - 1].threshold;
        let hAxisMaxValue = lastThreshold + lastThreshold / 4;

        return {
            ticks: ticks,
            textStyle: {
                fontSize: 12
            },
            viewWindow: {
                max: hAxisMaxValue,
                min: 0
            }
        };
    };

    let createTick = function(increment) {
        let thresholdDesc = '';

        if (increment.threshold >= 1000) {
            thresholdDesc += increment.threshold / 1000 + 'k';
        } else {
            thresholdDesc += increment.threshold;
        }

        let incrementDesc = increment.rebatePercentage + '% (\u00A3' + thresholdDesc + ')';

        return {
            v: increment.threshold,
            f: incrementDesc
        };
    };

    return (
        <>
            {rebateDetails ? (
                <div className={'rebate-details'}>
                    <h2>Rebates Chart</h2>
                    <div>
                        <span>
                            Rebate info (week {rebateDetails.currentWeek} of {rebateDetails.numberOfWeeks}) -
                            Predicted Rebate Value of £{rebateDetails.predictedRebate}*
                        </span>
                    </div>
                    <div>
                        <Chart
                            chartType="BarChart"
                            loader={<div>Loading Chart</div>}
                            data={[createRebateLegend(), createRebateSeries()]}
                            options={{
                                annotations: {
                                    textStyle: {
                                        fontSize: 12
                                    },
                                    stem: 20
                                },
                                chartArea: {
                                    left: 0,
                                    top: 40,
                                    width: '100%',
                                    height: '40%'
                                },
                                width: 950,
                                height: 200,
                                legend: {
                                    position: 'top',
                                    textStyle: {
                                        fontSize: 12
                                    }
                                },
                                isStacked: true,
                                series: [
                                    {
                                        color: '#00573D'
                                    },
                                    {
                                        color: '#86BBD8'
                                    },
                                    {
                                        color: '#D3D4D9'
                                    }
                                ],
                                hAxis: createRebateChartHorizontalAxis(),
                                bars: 'horizontal',
                                bar: {
                                    groupWidth: '60%'
                                },
                                animation: {
                                    duration: 1000,
                                    startup: true,
                                    easing: 'out'
                                }
                            }}
                        />
                    </div>
                    <div>
                        <span>
                            *Stores with a tobacco participation of greater than 50% will have their rebate
                            capped at a maximum of 4%
                        </span>
                    </div>
                    {!rebateDetails.ni && (
                        <div>
                            <span>
                                *This rebate chart is for Central Distribution Purchases only and Excludes
                                Cash and Carry and Direct to Store (DTS) Purchases.
                            </span>
                        </div>
                    )}
                    {rebateDetails.ni && (
                        <div>
                            <span>
                                **Projected spend for the current rebate period is calculated based on direct
                                sales for the previous rebate period.
                            </span>
                        </div>
                    )}
                </div>
            ) : (
                <LoadingSpinner />
            )}
        </>
    );
}
